import { Component, ChangeDetectionStrategy, Input, HostListener, HostBinding, inject } from "@angular/core";
import { UniversalModalService } from "@modules/+new/shared/universal-modal/universal-modal.service";
import { ActivatedRoute, NavigationEnd, Router, Scroll } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { distinctUntilKeyChanged, filter, map, tap } from "rxjs/operators";
import { combineLatest, Observable, Subscription } from "rxjs";
import { OpenModalDefinition } from "@modules/+new/shared/universal-modal/entities/modal-definition.interface";
import { ModalCloseReason } from "@modules/+new/shared/universal-modal/entities/modal-close-reason.enum";
import { ROUTER_OUTLET_NAME } from "@modules/+new/shared/universal-modal/entities/magic";
import { PresetsPayloads } from "@modules/+new/shared/universal-modal/entities/presets-payloads.type";
import { FEAT_GATES, StatsigClientService } from "@core-services/statsig/statsig.service";

@UntilDestroy()
@Component({
	selector: "modal-holder",
	template: `@if (shouldOpen$ | async) {
		<div class="modal-holder view-port" [class.hasTopClearance]="isGlobalBannerEnabled$ | async">
			<modal-render></modal-render>
		</div>
	}`,
	styleUrls: ["./modal-holder.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ModalHolderComponent {
	@Input() public forOutlet: boolean = true;
	public allowBackdropCancel: boolean = false;
	public isOpen: boolean = false;

	private statsigClient = inject(StatsigClientService);

	@HostBinding("class.is-open") get classOpen(): boolean {
		return this.isOpen;
	}

	public isGlobalBannerEnabled$: Observable<boolean> = this.statsigClient.isFeatureActive(
		FEAT_GATES.UK_FINPROM_GLOBAL_WARNING
	);

	/***
	 * @description this observable checks if this component should handle logic or not.
	 * it ties with router-outlet. it could be expanded to additional outlets in the future.
	 */
	public shouldOpen$: Observable<boolean> = this.service.current$.pipe(
		untilDestroyed(this),
		map((node) => {
			const { url, options } = node?.definition || {};
			const { allowBackdropCancel } = options || {};
			this.allowBackdropCancel = allowBackdropCancel;
			return node?.definition && ((url && this.forOutlet) || (!url && !this.forOutlet));
		}),
		tap((condition) => {
			this.isOpen = condition;
		})
	);

	constructor(
		private service: UniversalModalService,
		private route: ActivatedRoute,
		private router: Router
	) {
		this.forOutlet && this.listenRoutes();
	}

	public isUniversalModalEscape$: Observable<boolean> = this.service.close$.pipe(
		filter((val) => !!val),
		map((val) => val.reason === "escape" || val.reason === "cancel")
	);

	public isModalClosed$: Observable<boolean> = this.service.close$.pipe(
		filter((val) => !!val),
		map(
			(val) =>
				val.reason === ModalCloseReason.escape ||
				val.reason === ModalCloseReason.cancel ||
				val.reason === ModalCloseReason.close
		)
	);

	private filterRoute: ([RouterEvent, OpenModalDefinition]) => boolean = ([event, data]) =>
		event instanceof NavigationEnd && (!!data.component || !!data.preset);

	private listenRoutes = (): Subscription =>
		combineLatest([this.router.events, this.route.data])
			.pipe(
				untilDestroyed(this),
				map(([event, data]) => (event instanceof Scroll ? [event.routerEvent, data] : [event, data])),
				filter(this.filterRoute),
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				distinctUntilKeyChanged<any>("id"),
				map(
					([event, data]): OpenModalDefinition => ({
						...(data as OpenModalDefinition),
						url: (event as NavigationEnd).urlAfterRedirects.match(`${ROUTER_OUTLET_NAME}\:`)
							? (event as NavigationEnd).urlAfterRedirects
							: null
					})
				)
			)
			.subscribe((definition: OpenModalDefinition) => {
				const { data, preset, transforms } = definition;
				preset
					? this.service.openPreset(preset, data as PresetsPayloads, transforms)
					: this.service.open(definition);
			});

	@HostListener("mousedown", ["$event"])
	public onBackdropClick(event: MouseEvent): void {
		const target = event.target as HTMLElement;
		const insideRender = !!target.closest("modal-render");
		const insideViewPort = !!target.closest(".view-port");

		if (!this.canCancelOnBackdropClick(insideRender, insideViewPort)) {
			return;
		}

		this.service.close(ModalCloseReason.cancel);
	}

	@HostListener("document:keydown.escape")
	public onEscapeKeyDown(): void {
		this.service.close(ModalCloseReason.escape);
	}

	private canCancelOnBackdropClick(insideModalRender: boolean, insideViewPort: boolean): boolean {
		return this.allowBackdropCancel && !insideModalRender && !!insideViewPort;
	}
}
